import React, { useEffect, useRef, useState } from 'react';
import ReactDom from 'react-dom';
import { useSelector, Provider } from 'react-redux';
import PropTypes from 'prop-types';
import { loadModules } from 'esri-loader';
import Settings from './settings';
import { useComponentMount } from 'utilities/hooks.util';
import { Loading, Button } from 'carbon-components-react';
import { isBrowser } from 'react-device-detect';
import { selectBaseMap, selectBaseMapTheme } from 'redux/app/selectors';
import { MAP_DEFAULT_VAR } from 'types';
import { findBasemapByIdentifier } from 'utilities/basemap-migration.util';
import {
  debugLog,
  debugWarn,
  isBasemapDebugEnabled
} from 'utilities/basemap-debug.util';
import {
  registerMapInstance,
  unregisterMapInstance,
  getCurrentMapInstance
} from 'utilities/map-instance.util';
import {
  Add16,
  Subtract16,
  SendAlt16,
  Home16,
  Location16
} from '@carbon/icons-react';
import {
  zoomToCenter,
  zoomToFeature,
  hoverPin,
  removePin,
  removePinAndBuffer,
  restoreViewToClonedShape
} from './helper';
import { BaseMapSwitcherButton } from 'components/BaseMapSwitcher';
import MapLocationSearch from 'components/MapLocationSearchV2';

import { configureStore as store } from '../../redux/store';

let isECContacts = false;
let ecCallback: any = null;

const onClickCurrentLocation = () => {
  navigator.geolocation.getCurrentPosition((position) => {
    const center = [position.coords.longitude, position.coords.latitude];
    zoomToCenter(center, isECContacts, ecCallback);
  });
};

const onClickDropPin = () => {
  removePinAndBuffer();
  const { loadedSketchViewModel } = window;
  loadedSketchViewModel.create('point', { mode: 'click' });
};

const onClickHome = () => {
  const { mapOriginalExtent } = window;
  if (mapOriginalExtent) {
    zoomToFeature(mapOriginalExtent);
  }
};

const CurrentLocButton: React.FC = () => {
  return (
    <Button
      kind="tertiary"
      size="small"
      renderIcon={SendAlt16}
      iconDescription="My current location"
      tooltipPosition="right"
      hasIconOnly
      className="map--current-loc-button"
      onClick={onClickCurrentLocation}
    />
  );
};

const DropPinButton: React.FC = () => {
  return (
    <Button
      kind="tertiary"
      size="small"
      renderIcon={Location16}
      iconDescription="Drop pin"
      tooltipPosition="right"
      hasIconOnly
      className="map--current-loc-button"
      onClick={onClickDropPin}
    />
  );
};

const HomeButton: React.FC = () => {
  return (
    <Button
      kind="tertiary"
      size="small"
      renderIcon={Home16}
      iconDescription="Home"
      tooltipPosition="right"
      hasIconOnly
      className="map--home-button"
      onClick={onClickHome}
    />
  );
};

const Map: React.FC<Props> = (props) => {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const activeToolRef = useRef<string>('');
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const {
    modules,
    AUS_Extent,
    wkid,
    polygonSymbol,
    polygonSymbolDarkCompleteDraw
  } = Settings;
  const {
    map,
    mapView,
    extent,
    spatialReference,
    graphic,
    graphicsLayer,
    sketchViewModel,
    geometryEngine,
    polygon,
    config,
    basemap,
    basemapStyle,
    webMap,
    layerList,
    expand
  } = modules;
  const [isLoaded, setIsLoaded] = useState(false);
  const [isDualLoading, setIsDualLoading] = useState(false);
  const {
    fixedBaseMap,
    showCurrentLocationButton,
    showHomeButton,
    extraModules,
    onSelect,
    onCompleteDrawing,
    onCancelDrawing,
    onEditDrawingStart,
    onReady,
    showSearchButton,
    showbaseMapButton,
    onOpenBasemap,
    showDropPinButton,
    onDropPin,
    ecCurrentLocCB,
    resetECContacts,
    maxZoomLevel,
    onDrawing
  } = props;
  const defaultModules = [
    map,
    mapView,
    extent,
    spatialReference,
    graphic,
    graphicsLayer,
    sketchViewModel,
    geometryEngine,
    polygon,
    config,
    basemap,
    basemapStyle,
    webMap,
    layerList,
    expand
  ];
  // Single-source of truth for dual loading:
  // Only perform dual loading in the Update effect (after basemapOptions exist).
  // During Init, render a temporary/cheap basemap and skip swapping to WebMap.
  const singleSourceDualLoading = true;
  const modulesPrepareToLoad = extraModules
    ? [...defaultModules, ...extraModules]
    : [...defaultModules];
  const moduleNamespace = modulesPrepareToLoad.map((m) => m.namespace);
  const moduleKey = modulesPrepareToLoad.map((m) => m.key);
  const baseMap = useSelector(selectBaseMap);
  const baseMapTheme = useSelector(selectBaseMapTheme);
  const basemapOptions = useSelector(
    (state: any) => state.mapCache?.basemapOptions
  );
  const initialBasemapAppliedRef = useRef(false);

  isECContacts = showDropPinButton || false;
  ecCallback = ecCurrentLocCB || null;

  useEffect(() => {
    const { loadedSketchViewModel, loadedMap } = window;
    if (loadedSketchViewModel) {
      loadedSketchViewModel.polygonSymbol =
        baseMapTheme === 'dark' ? polygonSymbolDarkCompleteDraw : polygonSymbol;
      loadedSketchViewModel.activeLineSymbol = {
        type: 'simple-line',
        style: 'short-dash',
        color: baseMapTheme === 'dark' ? '#ffffff' : '#002d9c',
        width: 1
      };
      loadedSketchViewModel.activeVertexSymbol = {
        type: 'simple-marker',
        style: 'circle',
        size: 3,
        color: baseMapTheme === 'dark' ? '#ffffff' : '#002d9c',
        outline: {
          style: 'none'
        }
      };
      loadedSketchViewModel.activeFillSymbol = {
        type: 'simple-fill',
        style: 'solid',
        color:
          baseMapTheme === 'dark'
            ? 'rgba(255, 255, 255, 0.4)'
            : 'rgba(82, 82, 82, 0.15)',
        outline: {
          style: 'none'
        }
      };
      loadedSketchViewModel.polylineSymbol = {
        type: 'simple-line',
        style: 'solid',
        color: baseMapTheme === 'dark' ? '#ffffff' : '#002d9c',
        width: 1
      };
      loadedSketchViewModel.vertexSymbol = {
        type: 'simple-marker',
        style: 'circle',
        size: 3,
        color: baseMapTheme === 'dark' ? '#ffffff' : '#002d9c',
        outline: {
          style: 'none'
        }
      };
    }

    // Update existing graphics in the drawing layer to match the new theme
    if (loadedMap) {
      const drawingLayer = loadedMap.findLayerById('drawing-layer');
      const clonedShapeLayer = loadedMap.findLayerById('cloned-shape-layer');

      if (drawingLayer && drawingLayer.graphics) {
        const newSymbol =
          baseMapTheme === 'dark'
            ? polygonSymbolDarkCompleteDraw
            : polygonSymbol;

        drawingLayer.graphics.forEach((graphic: any) => {
          if (graphic.geometry && graphic.geometry.type === 'polygon') {
            graphic.symbol = newSymbol;
          }
        });
        // Trigger layer refresh to ensure visual update
        drawingLayer.emit('change');
      }

      if (clonedShapeLayer && clonedShapeLayer.graphics) {
        const newSymbol =
          baseMapTheme === 'dark'
            ? polygonSymbolDarkCompleteDraw
            : polygonSymbol;

        clonedShapeLayer.graphics.forEach((graphic: any) => {
          if (graphic.geometry && graphic.geometry.type === 'polygon') {
            graphic.symbol = newSymbol;
          }
        });
        // Trigger layer refresh to ensure visual update
        clonedShapeLayer.emit('change');
      }
    }
  }, [baseMapTheme, polygonSymbol, polygonSymbolDarkCompleteDraw]);

  /**
   * Apply persisted basemap from Redux after map loads and basemapOptions are available
   * This handles the race condition where the map initializes before Redux rehydrates
   * or before basemapOptions are loaded from the feature service
   *
   * IMPORTANT (SENT-2684): This effect should NOT call onReady() when the basemap is changed
   * by the user after initial load. Only the initial basemap application should trigger onReady().
   */
  useEffect(() => {
    // Skip if:
    // - Map not loaded yet
    // - No baseMap value from Redux
    // - Using fixed basemap (prop overrides Redux state)
    // - BasemapOptions not loaded yet (need them to look up config)
    if (
      !isLoaded ||
      !baseMap ||
      fixedBaseMap ||
      !basemapOptions ||
      basemapOptions.length === 0
    ) {
      return;
    }

    const { loadedView, loadedMap } = window;
    if (!loadedView || !loadedMap) {
      return;
    }

    // SENT-2684: After the initial basemap is applied, any subsequent changes are user-initiated.
    // The basemap switcher panel already handles the dual loading, so we should skip it here
    // to avoid re-initializing the enquiry flow which causes auto-submit.
    if (initialBasemapAppliedRef.current) {
      // This is a user-initiated basemap change - skip the entire effect
      // The basemap switcher panel already handles the basemap change
      debugLog(
        '[Map Update] Skipping basemap update - user-initiated change already handled by panel'
      );
      return;
    }

    // Mark as applied after first run
    initialBasemapAppliedRef.current = true;

    // Load required ArcGIS modules
    loadModules([
      'esri/Basemap',
      'esri/support/BasemapStyle',
      'esri/WebMap'
    ]).then(([Basemap, BasemapStyle, WebMap]: any[]) => {
      if (!isMounted.current) return;
      // Find basemap config
      const basemapItem = findBasemapByIdentifier(baseMap, basemapOptions);
      if (!basemapItem) {
        debugWarn(
          '[Map Update] No basemap config found for:',
          baseMap,
          '- keeping current basemap'
        );
        return;
      }

      const config = {
        name: basemapItem.label,
        styleId: basemapItem.styleId,
        webmapId: basemapItem.webmapId
      };

      debugLog('[Map Update] Applying persisted basemap from Redux:', {
        id: baseMap,
        name: config.name,
        styleId: config.styleId,
        webmapId: config.webmapId
      });

      // Handle dual loading (style + webmap)
      if (config.styleId && config.webmapId) {
        debugLog('[Map Update] Dual loading: WebMap + BasemapStyle override');
        // Hide the map until the WebMap (with operational layers) is fully ready
        setIsDualLoading(true);
        const webMapInstance = new WebMap({
          portalItem: { id: config.webmapId }
        });
        webMapInstance
          .loadAll()
          .then(() => {
            if (!isMounted.current) return;
            debugLog('[Map Update] WebMap loaded successfully');
            webMapInstance.basemap = new Basemap({
              style: new BasemapStyle({ id: config.styleId })
            });

            // Preserve critical operational layers (drawing layer and cloned shape layer)
            // Check both the old map (loadedMap) and the view's current map, as layers might
            // have been added to the view's map after loadedMap was set
            const criticalLayerIds = [
              'drawing-layer',
              'cloned-shape-layer',
              'enquiries-feature-layer',
              'enquiries-shape-layer',
              'enquiries-point-layer',
              'enquiries-grid-layer',
              'all-enquiries-shape-layer',
              'all-enquiries-grid-layer',
              'referrals-feature-layer',
              'referrals-shape-layer',
              'referrals-grid-layer'
            ];
            debugLog(
              '[Map Update] Dual loading - checking for layers to preserve:',
              {
                currentMapLayers: loadedMap.layers
                  .toArray()
                  .map((l: any) => l.id),
                viewMapLayers:
                  loadedView.map?.layers?.toArray()?.map((l: any) => l.id) || []
              }
            );

            criticalLayerIds.forEach((layerId) => {
              // Check old map first
              let layer = loadedMap.findLayerById(layerId);
              // If not found, check view's current map (might have been added there)
              if (!layer && loadedView.map) {
                layer = loadedView.map.findLayerById(layerId);
                debugLog(
                  '[Map Update] Layer found in view map instead of loadedMap:',
                  layerId,
                  {
                    graphicsCount: layer?.graphics?.length || 0
                  }
                );
              }
              if (layer) {
                debugLog(
                  '[Map Update] Preserving layer during dual load:',
                  layerId
                );
                debugLog('[Map Update] Found and preserving layer:', layerId, {
                  graphicsCount: layer.graphics ? layer.graphics.length : 0,
                  source:
                    layer === loadedMap.findLayerById(layerId)
                      ? 'loadedMap'
                      : 'viewMap'
                });
                if (!webMapInstance.findLayerById(layerId)) {
                  webMapInstance.add(layer);
                  debugLog('[Map Update] Layer added to new webmap:', layerId);
                } else {
                  debugLog(
                    '[Map Update] Layer already exists in webmap, skipping:',
                    layerId
                  );
                }
              } else {
                debugLog('[Map Update] Layer not found to preserve:', layerId);
                debugLog(
                  '[Map Update] Layer not found in current map or view map:',
                  layerId
                );
              }
            });

            debugLog(
              '[Map Update] After preservation, new webmap layers:',
              webMapInstance.layers.toArray().map((l: any) => ({
                id: l.id,
                graphicsCount: l.graphics ? l.graphics.length : 0
              }))
            );

            // Replace the view's map with the fully loaded WebMap instance
            loadedView.map = webMapInstance;

            // Re-register the active map instance so downstream code uses the new map
            // and re-initialize operational layers via onReady.
            const current = getCurrentMapInstance();
            registerMapInstance(
              webMapInstance,
              loadedView,
              (current && current.modules) || {}
            );

            // Call onReady to notify consumers that the map is ready
            // Note: This only runs on initial basemap application, not user-initiated changes
            // (user changes are handled by the early return above - SENT-2684)
            if (typeof onReady === 'function') {
              onReady();
            }

            restoreViewToClonedShape();

            // Reveal the map now that the dual loading is complete
            setIsDualLoading(false);

            // Additional safeguard: After dual loading completes, verify cloned-shape-layer exists
            setTimeout(() => {
              const clonedShapeLayer = webMapInstance.findLayerById(
                'cloned-shape-layer'
              );
              if (
                clonedShapeLayer &&
                clonedShapeLayer.graphics &&
                clonedShapeLayer.graphics.length === 0
              ) {
                debugLog(
                  '[Map Update] Cloned shape layer exists but has no graphics after dual loading'
                );
              } else if (!clonedShapeLayer) {
                debugLog(
                  '[Map Update] Cloned shape layer not found after dual loading'
                );
              } else {
                debugLog(
                  '[Map Update] Cloned shape layer preserved with graphics:',
                  clonedShapeLayer.graphics.length
                );
                restoreViewToClonedShape();
              }
            }, 200);

            debugLog('[Map Update] Dual loading complete');
          })
          .catch((error: any) => {
            if (!isMounted.current) return;
            debugWarn('[Map Update] WebMap load failed:', error);
            setIsDualLoading(false);
            // Call onReady to ensure map is usable even if WebMap fails
            // Note: This only runs on initial basemap application (SENT-2684)
            if (typeof onReady === 'function') {
              onReady();
            }
          });
      }
      // Handle style-only basemaps
      else if (config.styleId) {
        debugLog('[Map Update] Using BasemapStyle:', config.styleId);
        loadedMap.basemap = new Basemap({
          style: new BasemapStyle({ id: config.styleId })
        });
      }
      // Handle webmap-only basemaps (state-specific basemaps)
      else if (config.webmapId) {
        debugLog(
          '[Map Update] Using Basemap with portalItem:',
          config.webmapId
        );
        const webmapBasemap = new Basemap({
          portalItem: { id: config.webmapId }
        });
        webmapBasemap
          .load()
          .then(() => {
            if (!isMounted.current) return;
            debugLog('[Map Update] Webmap basemap loaded successfully');
            loadedMap.basemap = webmapBasemap;
          })
          .catch((error: any) => {
            if (!isMounted.current) return;
            debugWarn('[Map Update] Webmap basemap load failed:', error);
            // Still try to set it
            loadedMap.basemap = webmapBasemap;
          });
      }
    });
  }, [isLoaded, baseMap, basemapOptions, fixedBaseMap, onReady]);

  useComponentMount(() => {
    loadModules(moduleNamespace, { css: true })
      .then((loadedModules: any[]) => {
        if (!isMounted.current) return;
        const mapModules: any = {};
        loadedModules.forEach((m, inx) => {
          mapModules[moduleKey[inx]] = m;
        });
        const {
          GraphicsLayer,
          Extent,
          SpatialReference,
          Map,
          MapView,
          SketchViewModel,
          Config,
          Basemap,
          BasemapStyle,
          WebMap,
          LayerList,
          Expand
        } = mapModules;

        // Always set API key - interceptors will replace it with session token for basemap requests
        Config.apiKey = process.env.REACT_APP_ESRI_API_KEY;

        const drawingLayer = new GraphicsLayer({ id: 'drawing-layer' });
        const extent = new Extent({
          ...AUS_Extent,
          spatialReference: new SpatialReference({ wkid })
        });

        // Create basemap using dual loading pattern (style + optional webmap)
        const basemapId = fixedBaseMap || baseMap || MAP_DEFAULT_VAR.BASEMAP;
        debugLog('[Map Init] Loading basemap:', basemapId);

        // Find basemap config from Redux state (feature service data)
        const basemapItem = findBasemapByIdentifier(basemapId, basemapOptions);
        var matchedConfig: any = null;

        if (basemapItem) {
          matchedConfig = {
            name: basemapItem.label,
            styleId: basemapItem.styleId,
            webmapId: basemapItem.webmapId
          };
          debugLog('[Map Init] Found basemap config from feature service:', {
            id: basemapId,
            name: matchedConfig.name,
            styleId: matchedConfig.styleId,
            webmapId: matchedConfig.webmapId
          });
        } else {
          // Fallback: Use static mapping for common basemaps if options not loaded yet
          debugLog(
            '[Map Init] Basemap options not available yet, using static config for:',
            basemapId
          );
          const staticBasemapConfigs: {
            [key: string]: { styleId: string };
          } = {
            streets: { styleId: 'arcgis/topographic/base' },
            imagery: { styleId: 'arcgis/imagery/standard' },
            imageryHybrid: { styleId: 'arcgis/imagery' }
          };
          if (staticBasemapConfigs[basemapId]) {
            matchedConfig = staticBasemapConfigs[basemapId];
            debugLog('[Map Init] Using static config:', matchedConfig);
          } else {
            debugWarn(
              '[Map Init] No config found for:',
              basemapId,
              '- using default imagery'
            );
          }
        }

        // Dual loading logic: Check if we need to load WebMap with layers
        var map: any = null;
        var needsDualLoading =
          matchedConfig &&
          matchedConfig.styleId &&
          matchedConfig.webmapId &&
          typeof WebMap !== 'undefined' &&
          typeof Basemap !== 'undefined' &&
          typeof BasemapStyle !== 'undefined';

        if (!singleSourceDualLoading && needsDualLoading && matchedConfig) {
          // Dual loading: Load WebMap for operational layers, override basemap with style
          debugLog('[Map Init] Dual loading: WebMap + BasemapStyle');
          debugLog('[Map Init] Loading WebMap:', matchedConfig.webmapId);

          // Create map with temporary basemap
          map = new Map({
            basemap: new Basemap({
              style: new BasemapStyle({ id: matchedConfig.styleId })
            }),
            layers: [drawingLayer]
          });

          // Load WebMap asynchronously with all its layers
          var webMapInstance = new WebMap({
            portalItem: { id: matchedConfig.webmapId }
          });

          webMapInstance
            .loadAll()
            .then(function () {
              if (!isMounted.current) return;
              debugLog('[Map Init] WebMap loaded successfully');
              debugLog(
                '[Map Init] WebMap layers:',
                webMapInstance.layers.length
              );

              // Override webmap's basemap with the BasemapStyle
              webMapInstance.basemap = new Basemap({
                style: new BasemapStyle({ id: matchedConfig.styleId })
              });

              // Preserve critical operational layers (drawing layer and cloned shape layer)
              // Check both the old map and the view's current map, as layers might
              // have been added to the view's map after the old map was set
              if (view.map) {
                const oldMap = view.map;
                const criticalLayerIds = [
                  'drawing-layer',
                  'cloned-shape-layer',
                  'enquiries-feature-layer',
                  'enquiries-shape-layer',
                  'enquiries-point-layer',
                  'enquiries-grid-layer',
                  'all-enquiries-shape-layer',
                  'all-enquiries-grid-layer',
                  'referrals-feature-layer',
                  'referrals-shape-layer',
                  'referrals-grid-layer'
                ];

                criticalLayerIds.forEach((layerId) => {
                  // Check old map first
                  let layer = oldMap.findLayerById(layerId);
                  // If not found, check view's current map (might have been added there)
                  if (!layer && view.map && view.map !== oldMap) {
                    layer = view.map.findLayerById(layerId);
                    debugLog(
                      '[Map Init] Layer found in view map instead of oldMap:',
                      layerId
                    );
                  }
                  if (layer) {
                    debugLog(
                      '[Map Init] Preserving layer during dual load:',
                      layerId
                    );
                    debugLog(
                      '[Map Init] Found and preserving layer:',
                      layerId,
                      {
                        graphicsCount: layer.graphics?.length || 0
                      }
                    );
                    if (!webMapInstance.findLayerById(layerId)) {
                      webMapInstance.add(layer);
                      debugLog(
                        '[Map Init] Layer added to new webmap:',
                        layerId
                      );
                    } else {
                      debugLog(
                        '[Map Init] Layer already exists in webmap, skipping:',
                        layerId
                      );
                    }
                  } else {
                    debugLog(
                      '[Map Init] Layer not found to preserve:',
                      layerId
                    );
                    debugLog(
                      '[Map Init] Layer not found in old map or view map:',
                      layerId
                    );
                  }
                });
              }

              // Replace the entire map with the webmap
              view.map = webMapInstance;

              // Re-register the map instance with the new webmap
              // This ensures components can add layers to the correct map
              registerMapInstance(webMapInstance, view, mapModules);

              debugLog(
                '[Map Init] Dual loading complete - WebMap with style override'
              );

              // Notify components that the map is ready with the new webmap
              // This allows them to re-initialize their layers
              typeof onReady === 'function' && onReady();

              restoreViewToClonedShape();

              // Additional safeguard: After dual loading completes, verify cloned-shape-layer exists
              setTimeout(() => {
                const clonedShapeLayer = webMapInstance.findLayerById(
                  'cloned-shape-layer'
                );
                if (
                  clonedShapeLayer &&
                  clonedShapeLayer.graphics &&
                  clonedShapeLayer.graphics.length === 0
                ) {
                  debugLog(
                    '[Map Init] Cloned shape layer exists but has no graphics after dual loading'
                  );
                } else if (!clonedShapeLayer) {
                  debugLog(
                    '[Map Init] Cloned shape layer not found after dual loading'
                  );
                } else {
                  debugLog(
                    '[Map Init] Cloned shape layer preserved with graphics:',
                    clonedShapeLayer.graphics.length
                  );
                  restoreViewToClonedShape();
                }
              }, 200);
            })
            .catch(function (error: any) {
              if (!isMounted.current) return;
              debugWarn('[Map Init] Failed to load WebMap layers:', error);
              // Ensure map is usable even if WebMap fails (fallback to basic map)
              typeof onReady === 'function' && onReady();
            });
        } else {
          // Standard loading: Use styleId or portalItem
          var basemapToUse = null;
          if (matchedConfig) {
            if (matchedConfig.styleId) {
              // Use BasemapStyle for style-based basemaps
              debugLog('[Map Init] Using BasemapStyle:', matchedConfig.styleId);
              basemapToUse = new Basemap({
                style: new BasemapStyle({ id: matchedConfig.styleId })
              });
            } else if (matchedConfig.webmapId) {
              // Use Basemap with portalItem for webmap-only basemaps (e.g., state basemaps)
              debugLog(
                '[Map Init] Using Basemap with portalItem (webmap):',
                matchedConfig.webmapId
              );
              basemapToUse = new Basemap({
                portalItem: {
                  id: matchedConfig.webmapId
                }
              });
            }
          } else {
            // No config found - use default imagery basemap style
            debugWarn(
              '[Map Init] No basemap config found for:',
              basemapId,
              '- using default imagery style'
            );
            basemapToUse = new Basemap({
              style: new BasemapStyle({ id: 'arcgis/imagery' })
            });
          }

          map = new Map({
            basemap: basemapToUse,
            layers: [drawingLayer]
          });
        }

        const view = new MapView({
          container: mapRef.current,
          map,
          extent: window.mapOriginalExtent || extent,
          constraints: {
            maxZoom: maxZoomLevel || 22,
            geometry: new Extent(Settings.AUS_Boundary_Constraint),
            minScale: Settings.mapMinScale
          }
        });

        const sketchViewModel = new SketchViewModel({
          view,
          polygonSymbol:
            baseMapTheme === 'dark'
              ? polygonSymbolDarkCompleteDraw
              : polygonSymbol,
          activeLineSymbol: {
            type: 'simple-line',
            style: 'short-dash',
            color: baseMapTheme === 'dark' ? '#ffffff' : '#002d9c',
            width: 1
          },
          activeVertexSymbol: {
            type: 'simple-marker',
            style: 'circle',
            size: 3,
            color: baseMapTheme === 'dark' ? '#ffffff' : '#002d9c',
            outline: {
              style: 'none'
            }
          },
          activeFillSymbol: {
            type: 'simple-fill',
            style: 'solid',
            color:
              baseMapTheme === 'dark'
                ? 'rgba(255, 255, 255, 0.4)'
                : 'rgba(82, 82, 82, 0.15)',
            outline: {
              style: 'none'
            }
          },
          polylineSymbol: {
            type: 'simple-line',
            style: 'solid',
            color: baseMapTheme === 'dark' ? '#ffffff' : '#002d9c',
            width: 1
          },
          vertexSymbol: {
            type: 'simple-marker',
            style: 'circle',
            size: 3,
            color: baseMapTheme === 'dark' ? '#ffffff' : '#002d9c',
            outline: {
              style: 'none'
            }
          },
          updatePolygonSymbol: {
            type: 'simple-fill',
            style: 'solid',
            color:
              baseMapTheme === 'dark' ? 'rgba(255, 255, 255, 0.5)' : '#002d9c',
            width: 1
          },
          updatePolylineSymbol: {
            type: 'simple-fill',
            style: 'solid',
            color:
              baseMapTheme === 'dark' ? 'rgba(255, 0, 255, 0.5)' : '#002d9c',
            width: 1
          },
          layer: drawingLayer
        });

        if (showHomeButton) {
          const homeButton = document.createElement('div');
          homeButton.className = 'map--home-button-container';
          view.ui.add(homeButton, 'top-left');
        }

        if (showSearchButton) {
          const searchButton = document.createElement('div');
          searchButton.className = 'map--search-container';
          view.ui.add(searchButton, 'top-left');
        }

        if (showCurrentLocationButton) {
          const currentLocButton = document.createElement('div');
          currentLocButton.className = 'map--current-loc-button-container';
          view.ui.add(currentLocButton, 'top-left');
        }

        if (showDropPinButton) {
          const dropPinButton = document.createElement('div');
          dropPinButton.className = 'map--drop-pin-button-container';
          view.ui.add(dropPinButton, 'top-left');
        }

        // Add LayerList widget to inspect layers (including webmap layers)
        // Only add if debug mode is enabled
        if (LayerList && Expand && isBasemapDebugEnabled()) {
          debugLog('[Map Init] Adding LayerList widget');
          const layerListWidget = new LayerList({
            view: view,
            listItemCreatedFunction: function (event: any) {
              const item = event.item;
              debugLog('[LayerList] Layer item created:', {
                title: item.title,
                layer: item.layer.title,
                visible: item.layer.visible
              });
            }
          });

          const layerListExpand = new Expand({
            view: view,
            content: layerListWidget,
            expandIconClass: 'esri-icon-layer-list',
            expandTooltip: 'Layer List'
          });

          view.ui.add(layerListExpand, 'top-right');
          debugLog('[Map Init] LayerList widget added to top-right');
        }

        view.on('click', (event: __esri.MapViewClickEvent) => {
          view.graphics.removeAll();
          view
            .hitTest({ x: event.x, y: event.y })
            .then((repsonse: any) => {
              typeof onSelect === 'function' && onSelect(repsonse?.results);
              if (
                Array.isArray(repsonse?.results) &&
                repsonse.results.length > 0
              ) {
                repsonse.results.forEach((r: any) => {
                  if (r?.graphic?.layer?.id === 'mis-pin-layer') {
                    if (showDropPinButton) {
                      removePinAndBuffer();
                      typeof resetECContacts === 'function' &&
                        resetECContacts();
                    } else {
                      removePin();
                    }
                  }
                });
              }
            })
            .catch((err: any) => debugWarn('hit test error', err.toString()));
        });

        view.on('pointer-move', (event: __esri.MapViewPointerMoveEvent) => {
          view
            .hitTest({ x: event.x, y: event.y })
            .then((repsonse: any) => {
              if (
                Array.isArray(repsonse?.results) &&
                repsonse.results.length > 0
              ) {
                let hasPin = false;
                repsonse.results.forEach((r: any) => {
                  if (r?.graphic?.layer?.id === 'mis-pin-layer') {
                    hasPin = true;
                    hoverPin(true, repsonse.screenPoint);
                  }
                });

                !hasPin && hoverPin(false, repsonse.screenPoint);
              }
            })
            .catch((err: any) => debugWarn('hit test error', err.toString()));
        });

        view.ui.padding = 0;

        isBrowser
          ? view.ui.move('zoom', 'bottom-left')
          : view.ui.remove('zoom');

        // Is is important for this to be after zoom widget definition
        if (showbaseMapButton) {
          const basemapButton = document.createElement('div');
          basemapButton.className = 'map--basemap-container';
          view.ui.add(basemapButton, 'bottom-left');
        }
        view.when(() => {
          if (isBrowser) {
            //add icon into zoom button after map view is ready
            const zoomInButton = view.ui
              .find('zoom')
              ?.domNode.getElementsByClassName('esri-icon-plus')
              ?.item(0);
            const zoomOutButton = view.ui
              .find('zoom')
              ?.domNode.getElementsByClassName('esri-icon-minus')
              ?.item(0);
            zoomInButton && ReactDom.render(<Add16 />, zoomInButton);
            zoomOutButton && ReactDom.render(<Subtract16 />, zoomOutButton);
          }

          if (showCurrentLocationButton) {
            const CLBContainer = document
              .getElementsByClassName('map--current-loc-button-container')
              .item(0);
            CLBContainer && ReactDom.render(<CurrentLocButton />, CLBContainer);
          }

          if (showHomeButton) {
            const HBContainer = document
              .getElementsByClassName('map--home-button-container')
              .item(0);
            HBContainer && ReactDom.render(<HomeButton />, HBContainer);
          }

          if (showDropPinButton) {
            const DPBContainer = document
              .getElementsByClassName('map--drop-pin-button-container')
              .item(0);
            DPBContainer &&
              ReactDom.render(
                <Provider store={store}>
                  <DropPinButton />
                </Provider>,
                DPBContainer
              );
          }

          if (showbaseMapButton) {
            const BasemapContainer = document
              .getElementsByClassName('map--basemap-container')
              .item(0);
            BasemapContainer &&
              ReactDom.render(
                <Provider store={store}>
                  <BaseMapSwitcherButton onOpenBasemap={onOpenBasemap} />
                </Provider>,
                BasemapContainer
              );
          }

          if (showSearchButton) {
            const SearchContainer = document
              .getElementsByClassName('map--search-container')
              .item(0);
            SearchContainer &&
              ReactDom.render(
                <Provider store={store}>
                  <MapLocationSearch />
                </Provider>,
                SearchContainer
              );
          }
        });

        const createEventHandler = (
          event: __esri.SketchViewModelCreateEvent
        ) => {
          switch (event.state) {
            case 'start':
              activeToolRef.current = '';
              break;
            case 'active':
              activeToolRef.current = event.tool;
              typeof onDrawing === 'function' &&
                onDrawing({
                  tool: event.tool,
                  graphic: event.graphic,
                  drawingLayer
                });
              break;
            case 'complete':
              // For Emergency Contacts page drop pin functionality
              if (showDropPinButton) {
                if (event.tool === 'point') {
                  drawingLayer.remove(event.graphic);
                  const geom = event.graphic.geometry;
                  typeof onDropPin === 'function' && onDropPin(geom);
                }
              } else {
                if (activeToolRef.current === event.tool) {
                  typeof onCompleteDrawing === 'function' &&
                    onCompleteDrawing({
                      graphic: event.graphic,
                      drawingLayer
                    });
                } else {
                  drawingLayer.remove(event.graphic);
                  typeof onCancelDrawing === 'function' &&
                    onCancelDrawing({
                      drawingLayer
                    });
                }
              }
              activeToolRef.current = '';
              break;
            case 'cancel':
              typeof onCancelDrawing === 'function' &&
                onCancelDrawing({
                  drawingLayer
                });
              break;
            default:
              break;
          }
        };

        const updateEventHandler = (
          event: __esri.SketchViewModelUpdateEvent
        ) => {
          if (
            event.state === 'complete' &&
            event.graphics.length > 0 &&
            typeof onCompleteDrawing === 'function'
          ) {
            onCompleteDrawing({
              graphics: event.graphics,
              drawingLayer
            });
          }
          if (
            event.state === 'start' &&
            typeof onEditDrawingStart === 'function'
          ) {
            onEditDrawingStart({
              graphics: event.graphics,
              drawingLayer
            });
          }
        };

        sketchViewModel.on('create', createEventHandler);
        sketchViewModel.on('update', updateEventHandler);

        // Always register map instance using centralized utility
        // This ensures the correct map is registered even when navigating between pages
        registerMapInstance(map, view, mapModules);
        window.loadedSketchViewModel = sketchViewModel;

        if (!isLoaded) {
          if (!window.mapOriginalExtent) {
            window.mapOriginalExtent = extent;
          }
        }

        // Call onReady only when we are NOT in the dual-loading path.
        // In dual-loading, onReady will be called after the WebMap replaces the map
        // to avoid initializing layers on a soon-to-be-replaced map which cancels requests.
        if (typeof onReady === 'function' && !needsDualLoading) {
          onReady();
        }
        setIsLoaded(true);
      })
      .catch(() => {});

    return () => {
      if (window.loadedView) {
        window.mapOriginalExtent = window.loadedView.extent;
        window.loadedMap.removeAll();
        window.loadedMap.destroy();
        window.loadedView.destroy();
        unregisterMapInstance();
        delete window.loadedMap;
        delete window.loadedView;
        delete window.loadedSketchViewModel;
        delete window.loadedMapModules;
      }
    };
  });

  // Call onReady when it changes (e.g., when navigating between pages)
  // This ensures layers are re-initialized even if the Map component doesn't remount
  // We use a ref to track if onReady has been called for the current instance
  const onReadyRef = React.useRef(onReady);
  React.useEffect(() => {
    // If onReady function reference changed and map is loaded, call it
    if (
      isLoaded &&
      typeof onReady === 'function' &&
      onReadyRef.current !== onReady
    ) {
      onReadyRef.current = onReady;
      onReady();
    }
  }, [onReady, isLoaded]);

  return (
    <div className={`map-container ${isLoaded ? '--loaded' : ''}`}>
      <div
        className="map"
        ref={mapRef}
        data-testid="map"
        style={{ visibility: isDualLoading ? 'hidden' : 'visible' }}
      />
      {(!isLoaded || isDualLoading) && (
        <div className="map-loader">
          <Loading description="Active loading indicator" withOverlay={false} />
        </div>
      )}
    </div>
  );
};

const propTypes = {
  fixedBaseMap: PropTypes.string,
  showCurrentLocationButton: PropTypes.bool,
  showHomeButton: PropTypes.bool,
  extraModules: PropTypes.array,
  onCompleteDrawing: PropTypes.func,
  onSelect: PropTypes.func,
  onCancelDrawing: PropTypes.func,
  onEditDrawingStart: PropTypes.func,
  onReady: PropTypes.func,
  showSearchButton: PropTypes.bool,
  showbaseMapButton: PropTypes.bool,
  onOpenBasemap: PropTypes.func,
  showDropPinButton: PropTypes.bool,
  onDropPin: PropTypes.func,
  ecCurrentLocCB: PropTypes.func,
  resetECContacts: PropTypes.func,
  maxZoomLevel: PropTypes.number,
  onDrawing: PropTypes.func
};

type Props = PropTypes.InferProps<typeof propTypes>;

Map.propTypes = propTypes;

Map.defaultProps = {
  showCurrentLocationButton: true,
  showHomeButton: false,
  showDropPinButton: false,
  extraModules: []
};

export default React.memo(Map);
